import { Box, CircularProgress, IconButton, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { tokens } from '../../../global/theme/tokens';
import DateLabel from '../../label/DateLabel';
import { formatBytes } from '../explorer/MediaInfo';
import {
  entryKind,
  entryLabel,
  folderPurposes,
  listingToEntries,
  parentPath,
} from './explorerUtils';

const GRID_COLUMNS = 'minmax(200px, 1fr) 90px 150px 130px';

/**
 * Flat result list for the scoped (recursive) search. Entries carry their
 * full path, so selection, preview, context-menu actions and the info pane
 * all work exactly like in the regular views.
 */
const ExplorerSearchResults = ({
  results,
  isSearching,
  selected,
  onSelect,
  onOpen,
  onContextMenu,
  onJumpTo,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const entries = listingToEntries(results);

  return (
    <Box className="flex-1 min-h-0 flex flex-col overflow-x-auto">
      <Box sx={{ minWidth: '620px' }} className="flex flex-col flex-1 min-h-0">
        {/* Header */}
        <Box
          className="px-3 py-1"
          sx={{
            display: 'grid',
            gridTemplateColumns: GRID_COLUMNS,
            gap: '8px',
            borderBottom: `1px solid ${colors.glass}`,
          }}
        >
          {[t('Name'), t('Size'), t('Kind'), t('Modified')].map(
            (label, index) => (
              <Typography
                key={label}
                variant="caption"
                sx={{ opacity: 0.7, textAlign: index === 1 ? 'right' : 'left' }}
              >
                {label}
              </Typography>
            ),
          )}
        </Box>

        {/* Rows */}
        <Box className="flex-1 min-h-0 overflow-y-auto px-2 py-1">
          {entries.map((entry) => {
            const kind = entryKind(entry);
            const purposes = folderPurposes(entry);
            const isSelected = selected?.path === entry.path;
            const parent = parentPath(entry.path);
            return (
              <Box
                key={entry.path}
                onClick={(event) => {
                  event.stopPropagation();
                  onSelect(entry);
                }}
                onDoubleClick={() => onOpen(entry)}
                onContextMenu={(event) => onContextMenu(event, entry)}
                className="px-2 rounded-lg cursor-default group"
                sx={{
                  display: 'grid',
                  gridTemplateColumns: GRID_COLUMNS,
                  gap: '8px',
                  alignItems: 'center',
                  height: '44px',
                  backgroundColor: isSelected
                    ? colors.info + '30'
                    : 'transparent',
                  ':hover': {
                    backgroundColor: isSelected
                      ? colors.info + '40'
                      : colors.bgInput,
                  },
                }}
              >
                <Box className="flex flex-row items-center gap-2 min-w-0">
                  <kind.Icon sx={{ fontSize: '20px', color: kind.color }} />
                  <Box className="min-w-0 flex-1">
                    <Typography
                      variant="body2"
                      className="truncate"
                      sx={{ fontWeight: entry.isDir ? 500 : 400 }}
                    >
                      {entryLabel(entry)}
                    </Typography>
                    <Typography
                      variant="caption"
                      className="block truncate"
                      sx={{ opacity: 0.6 }}
                    >
                      {parent || '/'}
                    </Typography>
                  </Box>
                  {purposes.map((purpose) => (
                    <Typography
                      key={purpose.key}
                      variant="caption"
                      className="px-2 rounded-full whitespace-nowrap flex flex-row items-center gap-1"
                      sx={{
                        backgroundColor: colors.bgInput,
                        border: `1px solid ${colors.glass}`,
                      }}
                    >
                      <purpose.Icon sx={{ fontSize: '12px' }} />
                      {t(purpose.label)}
                    </Typography>
                  ))}
                  <IconButton
                    size="small"
                    className="opacity-0 group-hover:opacity-100"
                    title={t('Show in folder')}
                    onClick={(event) => {
                      event.stopPropagation();
                      onJumpTo(entry.isDir ? entry.path : parent);
                    }}
                  >
                    <FolderOpenIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </Box>
                <Typography
                  variant="caption"
                  className="monospace text-right"
                  sx={{ opacity: 0.7 }}
                >
                  {entry.isDir ? '—' : formatBytes(entry.size || 0)}
                </Typography>
                <Typography
                  variant="caption"
                  className="truncate"
                  sx={{ opacity: 0.7 }}
                >
                  {t(kind.label)}
                </Typography>
                <Typography
                  variant="caption"
                  className="monospace"
                  sx={{ opacity: 0.7 }}
                >
                  {entry.lastModified ? (
                    <DateLabel date={entry.lastModified * 1000} />
                  ) : (
                    '—'
                  )}
                </Typography>
              </Box>
            );
          })}

          {isSearching && !results && (
            <Box className="flex items-center justify-center py-16">
              <CircularProgress size="1.5rem" />
            </Box>
          )}

          {!isSearching && results && entries.length === 0 && (
            <Box className="flex flex-col items-center justify-center gap-2 py-16 opacity-60">
              <SearchOffIcon sx={{ fontSize: '34px' }} />
              <Typography variant="body2">{t('No results')}</Typography>
            </Box>
          )}

          {results?.truncated && (
            <Typography
              variant="caption"
              className="block text-center py-2"
              sx={{ opacity: 0.6 }}
            >
              {t('Too many results, only showing the first matches')}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ExplorerSearchResults;
